/**
 * Tab registry for the person record page. Pure module (no React) so
 * the RBAC visibility rule and the URL-param fallback are unit-testable
 * on their own.
 */

export const PERSON_TABS = ['profile', 'onboarding', 'kit', 'history', 'sap', 'admin'] as const;

export type PersonTabId = (typeof PERSON_TABS)[number];

export const PERSON_TAB_LABELS: Record<PersonTabId, string> = {
  profile: 'Profile',
  onboarding: 'Onboarding',
  kit: 'Kit & Certificates',
  history: 'History & Audit',
  sap: 'SAP',
  admin: 'Admin',
};

/** Roles that can see at least one panel on the Admin tab. The SAR
 *  export is currently visible to every role allowed on the page, so
 *  in practice the tab always shows; the rule is encoded so the tab
 *  disappears by itself if the export is ever gated tighter. */
const ADMIN_TAB_ROLES = ['admin', 'super_admin', 'coordinator', 'hr'];

/** The SAP push exposes bank/NI-adjacent data and fires LIVE hires, so
 *  the tab matches the ADMIN-only gate on its API routes. */
const SAP_TAB_ROLES = ['admin', 'super_admin'];

export function getVisibleTabs(roles: string[]): PersonTabId[] {
  return PERSON_TABS.filter((tab) => {
    if (tab === 'admin') return roles.some((r) => ADMIN_TAB_ROLES.includes(r));
    if (tab === 'sap') return roles.some((r) => SAP_TAB_ROLES.includes(r));
    return true;
  });
}

/**
 * Map the raw ?tab= query param onto a visible tab. Missing, unknown,
 * and role-hidden values all land on the default Profile tab.
 */
export function resolveTab(param: string | null, visible: PersonTabId[]): PersonTabId {
  if (param && (visible as string[]).includes(param)) return param as PersonTabId;
  return 'profile';
}
